import * as THREE from 'three';
import { glyphTexture } from './textures.js';
import { heightAt } from './terrain.js';
import { routeAt, ROUTE_LEN, BRIDGE, BRIDGE_SPAN } from './layout.js';
import { routeY } from './roads.js';
import { damp, dampAngle, clamp, lerp, smoothstep, TAU, rr, rnd, chance, wrapAngle, angleDiff, pick } from './util.js';

// ------------------------------------------------------------------ constants
const NODES = 12; // spine nodes
const DS = 0.27; // spacing between nodes (m)
const SPINE_LEN = DS * (NODES - 1);
const RINGS = 34;
const RAD = 12;
const OUTLINE = 0.032;
const GRAV = 17;

const C = {
  chestnut: new THREE.Color('#c4722f'),
  dark: new THREE.Color('#6e3c1c'),
  cream: new THREE.Color('#ffefd0'),
  tail: new THREE.Color('#7a4322'),
  tip: new THREE.Color('#1c120d'),
};

function radiusAt(u) {
  const bodyEnd = 0.62;
  if (u < bodyEnd) {
    const t = u / bodyEnd;
    return 0.22 + 0.075 * Math.sin(Math.min(1, t * 1.7) * Math.PI * 0.5) - 0.03 * t * t;
  }
  const t = (u - bodyEnd) / (1 - bodyEnd);
  return lerp(0.265, 0.03, Math.pow(t, 0.8)) + 0.05 * Math.sin(Math.PI * Math.min(1, t * 1.05)) * (1 - t * 0.5);
}

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _up = new THREE.Vector3(0, 1, 0);

// ------------------------------------------------------------------ the weasel
export class Weasel {
  constructor(scene, van, env) {
    this.scene = scene;
    this.van = van;
    this.env = env; // { sfx(name), puffs, kick(a) }
    this.head = new THREE.Vector3(); // head centre in the world
    this.yaw = 0;
    this.pos = new THREE.Vector3(); // ground-projected position (x,z) + hover height in y
    this.vel = new THREE.Vector3();
    this.vy = 0;
    this.speed = 0;
    this.sW = 0; // nearest route arc length
    this.lat = 0; // lateral offset from the route centre line
    this.state = 'chase';
    this.mood = 'chasing!';
    this.t = 0; // time in state
    this.stateDur = 0;
    this.cool = 4; // cool-down before the next stunt
    this.hopT = 0;
    this.gait = 0;
    this.burst = 0;
    this.burstCool = rr(2, 4);
    this.mouth = 0.2;
    this.eyeWide = 0;
    this.flip = 0; // radians
    this.spin = 0;
    this.dizzy = 0;
    this.arch = 0; // extra body arch
    this.wig = 0.15;
    this.riding = false;
    this.rideT = 0;
    this.chaos = 0; // 0..1 HUD "chaos" meter
    this.lastVel = new THREE.Vector3();
    this.accel = new THREE.Vector3();
    this.landTimer = 0;
    this.dookTimer = 0;
    this.seed = rnd() * 100;

    this.spine = Array.from({ length: NODES }, () => new THREE.Vector3());
    this.trail = [];
    this.curve = new THREE.CatmullRomCurve3(this.spine, false, 'catmullrom', 0.5);

    this.root = new THREE.Group();
    scene.add(this.root);
    this._buildBody();
    this._buildHead();
    this._buildLegs();
    this._buildStars();

    this._spawn();
  }

  // ------------------------------------------------------------ construction
  _buildBody() {
    const n = RINGS * RAD;
    const pos = new Float32Array(n * 3);
    const nor = new Float32Array(n * 3);
    const col = new Float32Array(n * 3);
    const idx = [];
    const c = new THREE.Color();
    for (let i = 0; i < RINGS; i++) {
      const u = i / (RINGS - 1);
      for (let j = 0; j < RAD; j++) {
        const phi = (j / RAD) * TAU;
        const up = Math.cos(phi);
        const belly = smoothstep(-0.02, -0.5, up);
        c.copy(C.chestnut).lerp(C.dark, smoothstep(0.55, 1, up) * 0.55);
        c.lerp(C.cream, belly);
        c.lerp(C.tail, smoothstep(0.64, 0.86, u));
        c.lerp(C.tip, smoothstep(0.9, 0.955, u));
        const k = i * RAD + j;
        col[k * 3] = c.r; col[k * 3 + 1] = c.g; col[k * 3 + 2] = c.b;
      }
    }
    for (let i = 0; i < RINGS - 1; i++) {
      for (let j = 0; j < RAD; j++) {
        const a = i * RAD + j, b = i * RAD + ((j + 1) % RAD), cc = (i + 1) * RAD + j, d = (i + 1) * RAD + ((j + 1) % RAD);
        idx.push(a, b, cc, b, d, cc);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1000);
    this.bodyGeo = geo;
    this.body = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0 }));
    this.body.castShadow = true;
    this.body.frustumCulled = false;
    this.root.add(this.body);

    const hgeo = new THREE.BufferGeometry();
    hgeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3).setUsage(THREE.DynamicDrawUsage));
    hgeo.setIndex(idx);
    hgeo.boundingSphere = geo.boundingSphere;
    this.hullGeo = hgeo;
    this.hull = new THREE.Mesh(hgeo, new THREE.MeshBasicMaterial({ color: '#25150e', side: THREE.BackSide }));
    this.hull.frustumCulled = false;
    this.root.add(this.hull);

    this.frames = Array.from({ length: RINGS }, () => ({ p: new THREE.Vector3(), t: new THREE.Vector3(), n: new THREE.Vector3(), b: new THREE.Vector3() }));
  }

  _mat(color, rough = 0.55) {
    return new THREE.MeshStandardMaterial({ color, roughness: rough });
  }

  _outlined(mesh, s = 1.09) {
    const o = new THREE.Mesh(mesh.geometry, this.outlineMat);
    o.scale.setScalar(s);
    o.raycast = () => {};
    mesh.add(o);
    return mesh;
  }

  _buildHead() {
    this.outlineMat = new THREE.MeshBasicMaterial({ color: '#25150e', side: THREE.BackSide });
    const sph = (r, ws = 14, hs = 10) => new THREE.SphereGeometry(r, ws, hs);
    const H = new THREE.Group();
    this.headGroup = H;
    this.root.add(H);
    const chest = this._mat('#c4722f'), cream = this._mat('#ffefd0'), pink = this._mat('#ff9fb4');
    const black = new THREE.MeshStandardMaterial({ color: '#191316', roughness: 0.3 });
    const white = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.25 });

    const skull = new THREE.Mesh(sph(0.42), chest);
    skull.scale.set(0.98, 0.9, 1.05);
    skull.castShadow = true;
    H.add(this._outlined(skull, 1.07));

    const snout = new THREE.Mesh(sph(0.26), cream);
    snout.position.set(0, -0.1, 0.38);
    snout.scale.set(0.95, 0.78, 1.2);
    H.add(this._outlined(snout, 1.1));

    const nose = new THREE.Mesh(sph(0.085, 10, 8), black);
    nose.position.set(0, -0.02, 0.69);
    nose.scale.set(1.2, 0.85, 1);
    H.add(nose);
    const shine = new THREE.Mesh(sph(0.025, 6, 5), new THREE.MeshBasicMaterial({ color: '#ffffff' }));
    shine.position.set(-0.03, 0.02, 0.74);
    H.add(shine);

    for (const sx of [-1, 1]) {
      const cheek = new THREE.Mesh(sph(0.17), cream);
      cheek.position.set(sx * 0.2, -0.16, 0.3);
      H.add(cheek);
      const blush = new THREE.Mesh(new THREE.CircleGeometry(0.075, 10), new THREE.MeshBasicMaterial({ color: '#ff7f9a', transparent: true, opacity: 0.55 }));
      blush.position.set(sx * 0.35, -0.08, 0.3);
      blush.rotation.y = sx * 1.1;
      H.add(blush);
    }

    // ears
    this.ears = [];
    for (const sx of [-1, 1]) {
      const g = new THREE.Group();
      g.position.set(sx * 0.3, 0.34, -0.06);
      const e = new THREE.Mesh(sph(0.15), chest);
      e.scale.set(1, 1.05, 0.5);
      const inner = new THREE.Mesh(sph(0.1), pink);
      inner.scale.set(1, 1, 0.4);
      inner.position.z = 0.05;
      g.add(this._outlined(e, 1.12), inner);
      g.rotation.z = sx * -0.4;
      H.add(g);
      this.ears.push({ g, sx });
    }

    // eyes (googly)
    this.eyes = [];
    for (const sx of [-1, 1]) {
      const g = new THREE.Group();
      g.position.set(sx * 0.2, 0.2, 0.32);
      const ball = new THREE.Mesh(sph(0.21, 16, 12), white);
      ball.scale.set(1, 1.05, 0.92);
      g.add(this._outlined(ball, 1.09));
      const pupil = new THREE.Mesh(sph(0.093, 10, 8), black);
      pupil.position.z = 0.15;
      g.add(pupil);
      const glint = new THREE.Mesh(sph(0.03, 6, 5), new THREE.MeshBasicMaterial({ color: '#fff' }));
      pupil.add(glint);
      glint.position.set(0.03, 0.04, 0.075);
      // eyelid (for blinks / squints)
      const lid = new THREE.Mesh(sph(0.222, 14, 8, 0, TAU, 0, Math.PI / 2), chest);
      lid.scale.set(1, 1.05, 0.95);
      lid.rotation.x = Math.PI / 2;
      lid.visible = false;
      g.add(lid);
      const brow = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.06, 0.06), black);
      brow.position.set(sx * 0.0, 0.27, 0.06);
      g.add(brow);
      H.add(g);
      this.eyes.push({ g, pupil, brow, lid, sx, p: new THREE.Vector2(), v: new THREE.Vector2() });
    }

    // jaw + mouth interior + tongue + teeth
    this.jaw = new THREE.Group();
    this.jaw.position.set(0, -0.2, 0.15);
    const jaw = new THREE.Mesh(sph(0.22), cream);
    jaw.scale.set(0.95, 0.4, 1.35);
    jaw.position.set(0, -0.06, 0.24);
    this.jaw.add(this._outlined(jaw, 1.1));
    const inside = new THREE.Mesh(sph(0.2), new THREE.MeshStandardMaterial({ color: '#7a1f32', roughness: 0.8 }));
    inside.scale.set(0.9, 0.3, 1.25);
    inside.position.set(0, 0.0, 0.26);
    this.jaw.add(inside);
    this.tongue = new THREE.Mesh(sph(0.1), new THREE.MeshStandardMaterial({ color: '#ff6f8f', roughness: 0.5 }));
    this.tongue.scale.set(0.9, 0.35, 1.7);
    this.tongue.position.set(0, 0.0, 0.34);
    this.jaw.add(this.tongue);
    H.add(this.jaw);
    for (const sx of [-1, 1]) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.13, 0.04), new THREE.MeshStandardMaterial({ color: '#fffdf4', roughness: 0.3 }));
      tooth.position.set(sx * 0.045, -0.15, 0.66);
      H.add(tooth);
    }
    // whiskers
    this.whiskers = [];
    const wm = new THREE.MeshBasicMaterial({ color: '#fdf6e6' });
    for (const sx of [-1, 1]) {
      for (let k = 0; k < 3; k++) {
        const w = new THREE.Mesh(new THREE.CylinderGeometry(0.008, 0.004, 0.42, 4), wm);
        w.geometry.translate(0, 0.21, 0);
        const g = new THREE.Group();
        g.position.set(sx * 0.16, -0.07 - k * 0.03, 0.62);
        g.rotation.z = sx * -(Math.PI / 2 - 0.25 + k * 0.28 - 0.3);
        g.rotation.y = sx * (0.2 + k * 0.15);
        g.add(w);
        H.add(g);
        this.whiskers.push({ g, sx, k, base: g.rotation.z });
      }
    }
    // little tuft on the head
    const tuft = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.2, 5), chest);
    tuft.position.set(0, 0.42, 0.08);
    tuft.rotation.x = -0.3;
    H.add(tuft);
    this.tuft = tuft;
  }

  _buildLegs() {
    this.legs = [];
    const mat = this._mat('#b3642a');
    const footMat = this._mat('#5a3320');
    const legGeo = new THREE.CapsuleGeometry(0.075, 1, 4, 8);
    for (let i = 0; i < 4; i++) {
      const front = i < 2;
      const sx = i % 2 ? 1 : -1;
      const leg = new THREE.Mesh(legGeo, mat);
      leg.castShadow = true;
      const foot = new THREE.Mesh(new THREE.SphereGeometry(0.115, 10, 8), footMat);
      foot.scale.set(1, 0.7, 1.35);
      foot.castShadow = true;
      this.root.add(leg, foot);
      this.legs.push({ leg, foot, front, sx, fp: new THREE.Vector3(), phase: front ? 0 : 0.5 });
    }
  }

  _buildStars() {
    const tex = glyphTexture('★', { size: 96, font: '700 80px Arial, sans-serif', color: '#ffe14a', stroke: '#8a5a00', strokeW: 8 });
    this.stars = [];
    for (let i = 0; i < 4; i++) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false }));
      s.scale.setScalar(0.42);
      s.visible = false;
      this.scene.add(s);
      this.stars.push(s);
    }
  }

  // ------------------------------------------------------------ spawn
  _spawn() {
    this.sW = this.van.s - 16;
    const r = routeAt(this.sW);
    this.pos.set(r.x, this._floorAt(r.x, r.z, this.sW, 0) + 0.36, r.z);
    this.yaw = r.yaw;
    this.head.copy(this.pos);
    this.trail.length = 0;
    for (let i = 0; i < 90; i++) {
      const back = i * 0.1;
      this.trail.push(new THREE.Vector3(this.pos.x - Math.sin(this.yaw) * back, this.pos.y, this.pos.z - Math.cos(this.yaw) * back));
    }
  }

  // ------------------------------------------------------------ helpers
  _corridorMax(s) {
    // narrow on the bridge, roomy on the open road
    let d = Math.abs(s - BRIDGE.s);
    d = Math.min(d, ROUTE_LEN - d);
    const half = (BRIDGE_SPAN.s1 - BRIDGE_SPAN.s0) / 2;
    return lerp(2.4, 5.4, smoothstep(half + 1, half + 9, d));
  }

  _nearBridge() {
    let d = Math.abs(this.van.s - BRIDGE.s);
    d = Math.min(d, ROUTE_LEN - d);
    return d < (BRIDGE_SPAN.s1 - BRIDGE_SPAN.s0) / 2 + 16;
  }

  _floorAt(x, z, s, lat) {
    if (Math.abs(lat) < 3.6) return routeY(s);
    return heightAt(x, z);
  }

  _track() {
    // refine nearest route s around the last one; fall back to a global search if we got lost
    const search = (from, span, step) => {
      let best = from, bd = 1e9;
      for (let k = -span; k <= span; k++) {
        const s = from + k * step;
        const r = routeAt(s);
        const d = (r.x - this.pos.x) ** 2 + (r.z - this.pos.z) ** 2;
        if (d < bd) { bd = d; best = s; }
      }
      return { best, bd };
    };
    let res = search(this.sW, 14, 0.5);
    if (res.bd > 100) res = search(this.sW, 280, 1.0);
    this.sW = ((res.best % ROUTE_LEN) + ROUTE_LEN) % ROUTE_LEN;
    const r = routeAt(this.sW);
    this.lat = (this.pos.x - r.x) * r.tz + (this.pos.z - r.z) * -r.tx;
    this.routeR = r;
    return r;
  }

  _setState(s, dur = 0) {
    this.state = s;
    this.t = 0;
    this.stateDur = dur;
  }

  _reseedTrailFromSpine() {
    this.trail.length = 0;
    for (const p of this.spine) this.trail.push(p.clone());
    // extend backwards a little
    const last = this.spine[NODES - 1], prev = this.spine[NODES - 2];
    const d = _v.copy(last).sub(prev).normalize();
    for (let i = 1; i < 8; i++) this.trail.push(last.clone().addScaledVector(d, i * 0.3));
  }

  // ------------------------------------------------------------ behaviour
  update(dt, time, beat) {
    const van = this.van;
    this.t += dt;
    this.cool -= dt;
    this.burstCool -= dt;
    this.dookTimer -= dt;
    this.landTimer -= dt;

    // ----- route tracking
    const rt = this._track();
    const limit = this._corridorMax(this.sW);

    // ----- behaviour per state
    let wantSpeed = 0;
    this.arch = damp(this.arch, 0, 6, dt);
    let mouthT = 0.14, eyeT = 0.0, wigT = 0.14, archT = 0;
    this.flip = 0;
    this.spin = 0;
    this.riding = false;

    switch (this.state) {
      case 'chase': {
        this.mood = this.burst > 0 ? 'zoom zoom!' : 'chasing!';
        // where do we want to be?
        const gap = 5.5 + 3.5 * Math.sin(time * 0.31 + this.seed) + (this.burst > 0 ? -3.5 : 0);
        const sT = van.s - Math.max(2.2, gap);
        const tr = routeAt(sT);
        const latOff = 3.4 * Math.sin(time * 1.9 + this.seed) + 1.7 * Math.sin(time * 3.7 + this.seed * 2);
        const tx = tr.x + tr.tz * latOff, tz = tr.z - tr.tx * latOff;
        _v.set(tx - this.pos.x, 0, tz - this.pos.z);
        const dist = _v.length();
        _v.normalize();
        const cruise = van.speed * 0.9 + 2.0;
        wantSpeed = clamp(dist * 1.5, 0, 12.5) + (dist > 3 ? cruise * 0.5 : 0);
        if (this.burst > 0) wantSpeed *= 1.35;
        wantSpeed = Math.min(wantSpeed, 16.5);
        this.vel.x = damp(this.vel.x, _v.x * wantSpeed, 5.5, dt);
        this.vel.z = damp(this.vel.z, _v.z * wantSpeed, 5.5, dt);
        // random speed bursts
        if (this.burstCool <= 0 && this.burst <= 0) {
          this.burst = rr(1.2, 2.0);
          this.burstCool = rr(4, 8);
          this.env.sfx('wee');
        }
        if (this.burst > 0) {
          this.burst -= dt;
          mouthT = 0.85;
          eyeT = 0.6;
          this.chaos = damp(this.chaos, 0.8, 3, dt);
        }
        // stunts
        const toVan = Math.hypot(van.pos.x - this.pos.x, van.pos.z - this.pos.z);
        if (this.cool <= 0) {
          const rear = this.env.vanRelative(this.pos);
          if (toVan < 5.2 && van.speed > 1.5 && rear.z < -0.5 && chance(0.5)) {
            this._startLeap();
          } else if (toVan < 14 && chance(0.4) && van.state === 'serving') {
            this._setState('dance', rr(2.4, 3.6));
            this.env.sfx('dook');
          } else {
            const roll = rnd();
            if (roll < 0.3 && this.speed > 4) { this._setState('flip', 0.95); this.env.sfx('boing'); this.vy = 7.2; }
            else if (roll < 0.55) { this._setState('dance', rr(2.2, 3.2)); this.env.sfx('dook'); }
            else if (roll < 0.8) { this._startZoom(); }
            else { this._setState('spin', 1.25); this.vy = 4.5; this.env.sfx('boing'); }
          }
          this.cool = rr(4.5, 8.5);
        }
        break;
      }
      case 'dance': {
        this.mood = 'WAR DANCE!';
        // face the van, hop side to side on the beat
        const toV = Math.atan2(van.pos.x - this.pos.x, van.pos.z - this.pos.z);
        this.yaw = dampAngle(this.yaw, toV, 8, dt);
        const side = Math.sin(this.t * 9.5);
        const fx = Math.sin(this.yaw), fz = Math.cos(this.yaw);
        const sx = Math.cos(this.yaw), sz = -Math.sin(this.yaw);
        // keep pace with the van a little so we do not fall far behind
        const followV = clamp((van.speed) * 0.85, 0, 8);
        const rd = routeAt(van.s - 5.0);
        _v.set(rd.x - this.pos.x, 0, rd.z - this.pos.z);
        const dd = _v.length();
        _v.normalize();
        this.vel.x = damp(this.vel.x, sx * side * 3.2 + _v.x * Math.min(dd * 1.2, followV + 1.5), 9, dt);
        this.vel.z = damp(this.vel.z, sz * side * 3.2 + _v.z * Math.min(dd * 1.2, followV + 1.5), 9, dt);
        archT = 0.85;
        mouthT = 0.95;
        eyeT = 1;
        wigT = 0.5;
        this.chaos = damp(this.chaos, 1, 4, dt);
        // hop on every swing
        const hop = Math.abs(Math.sin(this.t * 9.5 * 0.5 * 2));
        this.hopBoost = hop * 0.55;
        if (this.dookTimer <= 0) { this.dookTimer = 0.33; this.env.sfx('dook'); }
        if (this.t > this.stateDur) this._setState('chase');
        break;
      }
      case 'zoom': {
        this.mood = 'ZOOMIES!';
        this.zAng += dt * 6.2 * this.zDir;
        const tx = this.zc.x + Math.cos(this.zAng) * this.zR, tz = this.zc.z + Math.sin(this.zAng) * this.zR;
        _v.set(tx - this.pos.x, 0, tz - this.pos.z);
        const d = _v.length();
        _v.normalize();
        const v = 13;
        this.vel.x = damp(this.vel.x, _v.x * Math.min(v, d * 14), 12, dt);
        this.vel.z = damp(this.vel.z, _v.z * Math.min(v, d * 14), 12, dt);
        // keep the circle drifting with the van
        this.zc.x += Math.sin(van.yaw) * van.speed * dt * 0.9;
        this.zc.z += Math.cos(van.yaw) * van.speed * dt * 0.9;
        mouthT = 0.9;
        eyeT = 0.8;
        wigT = 0.4;
        this.chaos = damp(this.chaos, 0.95, 4, dt);
        if (this.dookTimer <= 0) { this.dookTimer = 0.4; this.env.sfx('dook'); }
        if (this.t > this.stateDur) { this._setState('chase'); this.burst = 1.5; }
        break;
      }
      case 'flip': {
        this.mood = 'BACKFLIP!';
        this.flip = Math.min(1, this.t / this.stateDur) * TAU;
        const followV = van.speed * 1.0 + 2;
        this.vel.x = damp(this.vel.x, Math.sin(this.yaw) * followV, 3, dt);
        this.vel.z = damp(this.vel.z, Math.cos(this.yaw) * followV, 3, dt);
        mouthT = 1;
        eyeT = 1;
        this.chaos = damp(this.chaos, 0.9, 5, dt);
        if (this.t > this.stateDur) { this._setState('chase'); }
        break;
      }
      case 'spin': {
        this.mood = 'whirling!';
        this.spin = Math.min(1, this.t / this.stateDur) * TAU * 2;
        const followV = van.speed * 1.0 + 2;
        this.vel.x = damp(this.vel.x, Math.sin(this.yaw) * followV, 3, dt);
        this.vel.z = damp(this.vel.z, Math.cos(this.yaw) * followV, 3, dt);
        mouthT = 1;
        eyeT = 1;
        this.chaos = damp(this.chaos, 0.85, 5, dt);
        if (this.t > this.stateDur) this._setState('chase');
        break;
      }
      case 'leap': {
        this.mood = 'LEAP!';
        const k = clamp(this.t / this.stateDur, 0, 1);
        const target = this.env.roofPoint(_v3);
        const px = lerp(this.leapFrom.x, target.x, k), pz = lerp(this.leapFrom.z, target.z, k);
        const py = lerp(this.leapFrom.y, target.y, k) + Math.sin(k * Math.PI) * 2.6;
        this.vel.set((px - this.pos.x) / Math.max(dt, 1e-4), 0, (pz - this.pos.z) / Math.max(dt, 1e-4));
        this.pos.set(px, py, pz);
        this.vy = 0;
        this.leapY = py;
        mouthT = 1;
        eyeT = 1;
        this.chaos = damp(this.chaos, 1, 5, dt);
        if (k >= 1) {
          this._setState('ride', rr(6, 8));
          this.env.sfx('thud');
          this.env.kick(0.6);
          this.riding = true;
        }
        break;
      }
      case 'ride': {
        this.mood = 'hugging the cone';
        this.riding = true;
        mouthT = 0.9;
        eyeT = 0.5;
        this.chaos = damp(this.chaos, 1, 4, dt);
        if (this.dookTimer <= 0) { this.dookTimer = 0.45; this.env.sfx('dook'); }
        if (this.t > this.stateDur && !this._nearBridge()) this._startFling();
        break;
      }
      case 'fling': {
        this.mood = 'YEEEET!';
        this.vy -= GRAV * dt;
        this.pos.x += this.vel.x * dt;
        this.pos.z += this.vel.z * dt;
        this.pos.y += this.vy * dt;
        this.spinRoll += dt * 11;
        const floor = this._floorAt(this.pos.x, this.pos.z, this.sW, this.lat) + 0.36;
        mouthT = 1;
        eyeT = 1;
        if (this.pos.y <= floor && this.vy < 0) {
          this.pos.y = floor;
          this.vy = 0;
          this.vel.multiplyScalar(0.2);
          this._setState('dizzy', 3.0);
          this.env.sfx('splat');
          this.env.kick(0.5);
          this._dust(this.pos, 8);
        }
        break;
      }
      case 'dizzy': {
        this.mood = 'dizzy... *stars*';
        this.dizzy = 1;
        const wob = Math.sin(this.t * 3.1) * 0.9;
        this.yaw += dt * (Math.sin(this.t * 2.3) * 2.2 + 1.2);
        wantSpeed = 1.4;
        this.vel.x = damp(this.vel.x, Math.sin(this.yaw + wob) * wantSpeed, 3, dt);
        this.vel.z = damp(this.vel.z, Math.cos(this.yaw + wob) * wantSpeed, 3, dt);
        mouthT = 0.5 + 0.3 * Math.sin(this.t * 8);
        wigT = 0.55;
        archT = -0.15;
        this.chaos = damp(this.chaos, 0.2, 2, dt);
        if (this.dookTimer <= 0) { this.dookTimer = 0.55; this.env.sfx('dizzy'); }
        if (this.t > this.stateDur) { this._setState('chase'); this.dizzy = 0; this.cool = rr(2, 4); }
        break;
      }
    }
    if (this.state !== 'dizzy') this.dizzy = damp(this.dizzy, 0, 8, dt);
    if (this.state !== 'chase' && this.state !== 'dance' && this.state !== 'zoom') this.chaos = damp(this.chaos, this.state === 'ride' ? 1 : 0.5, 1, dt);
    if (this.state === 'chase' && this.burst <= 0) this.chaos = damp(this.chaos, 0.3, 1.2, dt);

    // ----- integrate motion (grounded states)
    const grounded = this.state !== 'leap' && this.state !== 'ride' && this.state !== 'fling';
    if (grounded) {
      this.pos.x += this.vel.x * dt;
      this.pos.z += this.vel.z * dt;
      // corridor clamp
      const r2 = this._track();
      let lat = this.lat;
      if (Math.abs(lat) > limit) {
        const over0 = lat - Math.sign(lat) * limit;
        // slide back toward the road instead of teleporting (e.g. after being flung far away)
        const over = Math.sign(over0) * Math.min(Math.abs(over0), 10 * dt + 0.02);
        this.pos.x -= r2.tz * over;
        this.pos.z -= -r2.tx * over;
        // kill the outward velocity
        const vOut = (this.vel.x * r2.tz + this.vel.z * -r2.tx) * Math.sign(lat);
        if (vOut > 0) {
          this.vel.x -= r2.tz * Math.sign(lat) * vOut;
          this.vel.z -= -r2.tx * Math.sign(lat) * vOut;
        }
      }
      const sp = Math.hypot(this.vel.x, this.vel.z);
      this.speed = sp;
      if (sp > 0.4 && this.state !== 'dance' && this.state !== 'dizzy' && this.state !== 'flip' && this.state !== 'spin') {
        this.yaw = dampAngle(this.yaw, Math.atan2(this.vel.x, this.vel.z), 11, dt);
      }
      // vertical: bounding gait + jump physics
      const floor = this._floorAt(this.pos.x, this.pos.z, this.sW, this.lat);
      const stride = clamp(0.9 + sp * 0.1, 0.9, 2.6);
      this.hopT += dt * (2.0 + sp * 0.32);
      let hopH = 0;
      if (this.state === 'chase') hopH = Math.pow(Math.abs(Math.sin(this.hopT * Math.PI)), 0.8) * (0.14 + Math.min(sp, 12) * 0.035);
      else if (this.state === 'dance') hopH = (this.hopBoost || 0);
      else if (this.state === 'zoom') hopH = Math.pow(Math.abs(Math.sin(this.hopT * Math.PI)), 0.8) * 0.38;
      else if (this.state === 'dizzy') hopH = 0.04;
      const base = floor + 0.36;
      if (this.vy !== 0 || this.pos.y > base + hopH + 0.05) {
        // airborne (flip / spin jumps)
        this.vy -= GRAV * dt;
        this.pos.y += this.vy * dt;
        if (this.pos.y <= base) { this.pos.y = base; if (this.vy < -3) { this.env.sfx('hop'); this._dust(this.pos, 4); } this.vy = 0; }
      } else {
        this.pos.y = damp(this.pos.y, base + hopH, 40, dt);
      }
      this.gait += sp * dt / stride;
    }

    // ----- eyes / mouth / ears easing
    this.mouth = damp(this.mouth, mouthT + (this.state === 'chase' ? Math.max(0, Math.sin(this.hopT * Math.PI * 2)) * 0.12 : 0), 12, dt);
    this.eyeWide = damp(this.eyeWide, eyeT, 8, dt);
    this.wig = damp(this.wig, wigT, 4, dt);
    this.arch = damp(this.arch, archT, 7, dt);
    if (this.state === 'fling') this.arch = 0;

    // ----- spine construction
    this._makeSpine(dt, time, beat);
    if (this.state === 'ride') this.pos.copy(this.spine[0]);

    // ----- head / body / legs
    this._updateHead(dt, time, beat);
    this._updateTube();
    this._updateLegs(dt, time);
    this._updateStars(dt, time);

    // trail recording (after the spine so the trail reflects the real head path)
    this.head.copy(this.spine[0]);
    if (!this.riding && this.state !== 'ride') {
      const last = this.trail[0];
      if (!last || this.head.distanceToSquared(last) > 0.0081) {
        this.trail.unshift(this.head.clone());
        if (this.trail.length > 110) this.trail.length = 110;
      }
    }

    this.accel.copy(this.vel).sub(this.lastVel).multiplyScalar(1 / Math.max(dt, 1e-4));
    this.lastVel.copy(this.vel);
  }

  /** The user clicked the weasel: it panics into a random stunt. */
  poke() {
    if (this.state === 'ride' || this.state === 'leap' || this.state === 'fling') return;
    this.env.sfx('boing');
    if (chance(0.5)) { this._setState('flip', 0.95); this.vy = 7.4; }
    else { this._setState('spin', 1.25); this.vy = 5; }
    this.cool = rr(4, 7);
  }

  _startZoom() {
    this._setState('zoom', rr(2.2, 3.0));
    this.zR = rr(2.3, 2.9);
    const rd = routeAt(this.sW);
    this.zDir = chance(0.5) ? 1 : -1;
    this.zc = new THREE.Vector3(this.pos.x, 0, this.pos.z);
    // circle centre a little off the road centre so it stays inside the corridor
    const off = clamp(-this.lat, -1, 1) * 0.5;
    this.zc.x += rd.tz * off;
    this.zc.z += -rd.tx * off;
    this.zAng = Math.atan2(this.pos.z - this.zc.z, this.pos.x - this.zc.x);
    this.env.sfx('wee');
  }

  _startLeap() {
    this._setState('leap', 0.72);
    this.leapFrom = this.pos.clone();
    this.vy = 0;
    this.env.sfx('boing');
  }

  _startFling() {
    const van = this.van;
    // launch toward the outside of the ring mostly (away from houses)
    const side = chance(0.8) ? 1 : -1;
    const cy = Math.cos(van.yaw), sy = Math.sin(van.yaw);
    const rx = -(-cy) * 0 + cy * side, rz = -sy * side; // van local +x in world (cos, -sin)
    this.vel.set(rx * rr(6.5, 8.5) + Math.sin(van.yaw) * van.speed * 0.9, 0, rz * rr(6.5, 8.5) + Math.cos(van.yaw) * van.speed * 0.9);
    this.vy = rr(7.5, 9.5);
    this.spinRoll = 0;
    this._reseedTrailFromSpine();
    this.pos.copy(this.spine[0]);
    this._setState('fling', 3);
    this.riding = false;
    this.env.sfx('yeet');
    this.env.kick(0.8);
    this.cool = rr(3, 5);
  }

  _dust(p, n) {
    for (let i = 0; i < n; i++) {
      const a = rnd() * TAU;
      this.env.puffs.emit(p.x + Math.cos(a) * 0.3, p.y - 0.2, p.z + Math.sin(a) * 0.3, Math.cos(a) * rr(0.6, 1.6), rr(0.3, 0.9), Math.sin(a) * rr(0.6, 1.6), rr(0.45, 0.8), 0.06, rr(0.16, 0.3));
    }
  }

  // ------------------------------------------------------------ pose
  _makeSpine(dt, time, beat) {
    const spine = this.spine;
    if (this.state === 'ride') {
      // coil around the ice-cream cone on the van roof
      const topper = this.env.topperFrame();
      const head = topper.head, th0 = topper.angle;
      const rideP = clamp(this.t / 0.5, 0, 1);
      const bop = Math.sin(beat * Math.PI * 2) * 0.05;
      for (let i = 0; i < NODES; i++) {
        const a = th0 - i * 0.54;
        const y = head.y - i * DS * 0.42 + (i === 0 ? bop : 0);
        const yRel = y - (topper.head.y - 1.65);
        // hug the waffle cone: radius follows the cone profile, then the scoops
        const coneR = yRel < 1.25 ? 0.05 + 0.5 * Math.max(yRel, 0) / 1.25 : 0.6;
        const R = coneR + radiusAt(i / (NODES - 1)) * 0.95 + 0.04;
        spine[i].set(head.x + Math.cos(a) * R, Math.max(y, topper.floorY + 0.3), head.z + Math.sin(a) * R);
      }
      return;
    }
    // walk the trail
    spine[0].copy(this.pos);
    let ti = 0, prev = spine[0], acc = 0;
    for (let i = 1; i < NODES; i++) {
      const want = i * DS;
      for (;;) {
        const nxt = this.trail[ti];
        if (!nxt) {
          const rem = want - acc;
          spine[i].set(prev.x - Math.sin(this.yaw) * rem, prev.y, prev.z - Math.cos(this.yaw) * rem);
          break;
        }
        const seg = prev.distanceTo(nxt);
        if (acc + seg >= want) {
          spine[i].lerpVectors(prev, nxt, (want - acc) / (seg || 1));
          break;
        }
        acc += seg;
        prev = nxt;
        ti++;
      }
    }
    // deformations in the body frame
    const fwdX = Math.sin(this.yaw), fwdZ = Math.cos(this.yaw);
    const sideX = Math.cos(this.yaw), sideZ = -Math.sin(this.yaw);
    const speedK = clamp(this.speed / 9, 0, 1);
    const hopPh = this.hopT;
    for (let i = 1; i < NODES; i++) {
      const u = i / (NODES - 1);
      const p = spine[i];
      // travelling arch (bounding back) — stronger while sprinting
      let arch = 0;
      if (this.state === 'chase' || this.state === 'zoom') {
        arch = (0.5 + 0.5 * Math.sin(TAU * (hopPh - u * 0.85))) * (0.05 + speedK * 0.24) * Math.sin(Math.min(1, u * 1.6) * Math.PI);
      }
      arch += this.arch * Math.sin(Math.min(1, u / 0.72) * Math.PI) * 0.8;
      p.y += arch;
      // side-to-side slither, mostly in the tail
      const wig = this.wig * (0.25 + u * 1.15) * Math.sin(TAU * (time * (0.9 + speedK * 1.4) - u * 0.95) + this.seed);
      p.x += sideX * wig;
      p.z += sideZ * wig;
      // tail flick
      if (u > 0.62) {
        p.y += Math.sin(time * 6 + u * 4) * 0.05 * (u - 0.6) * 4;
      }
    }
    // whole-body stunts around the body centre
    if (this.flip !== 0 || this.spin !== 0) {
      const pivot = _v.set(0, 0, 0);
      for (let i = 1; i < 7; i++) pivot.add(spine[i]);
      pivot.multiplyScalar(1 / 6);
      if (this.flip !== 0) {
        _v2.set(sideX, 0, sideZ);
        _q.setFromAxisAngle(_v2, -this.flip);
        for (let i = 0; i < NODES; i++) spine[i].sub(pivot).applyQuaternion(_q).add(pivot);
      }
      if (this.spin !== 0) {
        _q.setFromAxisAngle(_up, this.spin);
        for (let i = 0; i < NODES; i++) {
          spine[i].sub(pivot).applyQuaternion(_q).add(pivot);
          spine[i].y += 0.0;
        }
        pivot.y += 0;
      }
    }
    if (this.state === 'fling') {
      // barrel roll around the flight direction
      const pivot = _v.copy(spine[3]);
      _v2.set(this.vel.x, 0.001, this.vel.z).normalize();
      _q.setFromAxisAngle(_v2, this.spinRoll);
      for (let i = 0; i < NODES; i++) spine[i].sub(pivot).applyQuaternion(_q).add(pivot);
    }
    // keep the body above the ground
    for (let i = 1; i < NODES; i++) {
      const p = spine[i];
      const r = radiusAt(i / (NODES - 1));
      const gy = this.riding ? -1e9 : heightAt(p.x, p.z);
      const floor = Math.abs(this.lat) < 3.8 ? Math.max(routeY(this.sW), 0) : gy;
      const minY = (this.state === 'ride' ? -1e9 : floor) + r * 0.85;
      if (p.y < minY) p.y = minY;
    }
  }

  _updateHead(dt, time, beat) {
    const H = this.headGroup;
    const s0 = this.spine[0], s1 = this.spine[1], s2 = this.spine[2];
    // heading of the neck
    const fwd = _v.copy(s0).sub(s1).normalize();
    if (this.state === 'ride') {
      const van = this.van;
      const tp = this.env.topperFrame();
      // lick the scoop: head points toward the cone axis, bobbing
      fwd.set(tp.axis.x - s0.x, tp.axis.y - s0.y - 0.15 + Math.sin(time * 9) * 0.25, tp.axis.z - s0.z).normalize();
    }
    // basis
    _v3.copy(_up);
    if (this.state === 'fling' || this.flip !== 0) {
      // during flips derive the up from the body normal so the head rolls with it
      _v3.copy(this.frames[0].n);
    }
    const zAxis = fwd;
    const xAxis = _v2.copy(_v3).cross(zAxis);
    if (xAxis.lengthSq() < 1e-4) xAxis.set(1, 0, 0);
    xAxis.normalize();
    const yAxis = new THREE.Vector3().copy(zAxis).cross(xAxis);
    _m.makeBasis(xAxis, yAxis, zAxis);
    _q.setFromRotationMatrix(_m);
    // extra head motions
    const jitter = this.state === 'dance' ? Math.sin(time * 19) * 0.16 : this.state === 'dizzy' ? Math.sin(time * 3.3) * 0.35 : Math.sin(time * 2.1 + this.seed) * 0.05;
    const tilt = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      (this.state === 'chase' ? -Math.abs(Math.sin(this.hopT * Math.PI)) * 0.12 : 0) + (this.state === 'dance' ? Math.sin(time * 9.5) * 0.15 : 0),
      jitter,
      (this.state === 'dance' ? Math.sin(time * 9.5 + 1) * 0.35 : 0) + (this.state === 'dizzy' ? Math.sin(time * 2.7) * 0.3 : 0),
      'YXZ',
    ));
    _q.multiply(tilt);
    H.quaternion.copy(_q);
    H.position.copy(s0).addScaledVector(fwd, 0.2);
    H.position.y += 0.03;

    // ears back at speed
    for (const e of this.ears) {
      const back = clamp(this.speed / 10, 0, 1) * 0.55 + (this.state === 'dance' ? 0.3 : 0);
      e.g.rotation.x = -back + Math.sin(time * 14 + e.sx) * 0.06 * this.speed * 0.1;
      e.g.rotation.z = e.sx * -(0.4 + back * 0.3 + (this.state === 'dizzy' ? Math.sin(time * 5) * 0.4 : 0));
    }
    // jaw
    const m = clamp(this.mouth, 0, 1);
    this.jaw.rotation.x = m * 0.62;
    this.tongue.position.z = 0.34 + Math.max(0, m - 0.55) * 0.35 + (this.state === 'ride' ? (Math.sin(time * 10) * 0.5 + 0.5) * 0.15 : 0);
    this.tongue.position.y = -m * 0.04;
    this.tongue.scale.z = 1.7 + Math.max(0, m - 0.5) * 1.6;
    // whiskers
    for (const w of this.whiskers) {
      w.g.rotation.x = Math.sin(time * 9 + w.k + w.sx) * 0.1 * (0.5 + this.speed * 0.1);
    }
    this.tuft.rotation.z = Math.sin(time * 7) * 0.2;

    // googly eyes: pupils obey gravity + head acceleration
    const invQ = _q.clone().invert();
    const aL = _v.copy(this.accel).applyQuaternion(invQ);
    const gL = _v2.set(0, -1, 0).applyQuaternion(invQ);
    const eyeScale = 1 + this.eyeWide * 0.22 + (this.state === 'dizzy' ? 0.1 : 0);
    for (const e of this.eyes) {
      const acx = clamp(aL.x, -60, 60), acy = clamp(aL.y, -60, 60);
      e.v.x += (gL.x * 3.6 - acx * 0.02 * e.sx * 0 - acx * 0.02 - e.v.x * 5.0) * dt;
      e.v.y += (gL.y * 3.6 - acy * 0.02 - e.v.y * 5.0) * dt;
      if (this.state === 'dizzy' || this.state === 'fling' || this.state === 'spin') {
        // pupils orbit like crazy
        const a = time * (this.state === 'dizzy' ? 7 : 18) + e.sx * 1.2;
        e.p.set(Math.cos(a) * 0.065, Math.sin(a) * 0.065);
        e.v.set(0, 0);
      } else {
        e.p.x += e.v.x * dt;
        e.p.y += e.v.y * dt;
        // shove pupils around when hyper
        if (this.eyeWide > 0.3) { e.p.x += Math.sin(time * 31 + e.sx) * 0.004; e.p.y += Math.cos(time * 27 + e.sx) * 0.004; }
        const r = Math.hypot(e.p.x, e.p.y);
        const R = 0.085;
        if (r > R) {
          e.p.multiplyScalar(R / r);
          const nx = e.p.x / R, ny = e.p.y / R;
          const vn = e.v.x * nx + e.v.y * ny;
          if (vn > 0) { e.v.x -= nx * vn * 1.6; e.v.y -= ny * vn * 1.6; }
        }
      }
      e.pupil.position.set(e.p.x, e.p.y, 0.15);
      e.g.scale.setScalar(eyeScale);
      e.brow.rotation.z = e.sx * (-0.45 - this.eyeWide * 0.35 + Math.sin(time * 3 + e.sx) * 0.05);
      e.brow.position.y = 0.27 + this.eyeWide * 0.05;
    }
    // squash & stretch the head slightly with speed
    const st = 1 + clamp(this.speed / 16, 0, 0.12);
    H.scale.set(1 / Math.sqrt(st), 1 / Math.sqrt(st), st);
  }

  _updateTube() {
    // frames along the spine curve
    const cv = this.curve;
    const F = this.frames;
    let prevN = null;
    for (let i = 0; i < RINGS; i++) {
      const u = i / (RINGS - 1);
      const f = F[i];
      cv.getPoint(u, f.p);
      cv.getTangent(u, f.t);
      // tangent along the body from neck->tail; we want head-to-tail direction
      if (!prevN) {
        // world-up projected
        f.n.copy(_up).addScaledVector(f.t, -_up.dot(f.t));
        if (f.n.lengthSq() < 1e-3) f.n.set(1, 0, 0);
        if (this.flip !== 0 || this.state === 'fling' || this.state === 'ride') {
          // keep continuity when the body goes vertical
          if (this._lastN0) { f.n.copy(this._lastN0).addScaledVector(f.t, -this._lastN0.dot(f.t)); if (f.n.lengthSq() < 1e-4) f.n.copy(_up); }
        }
        f.n.normalize();
        this._lastN0 = this._lastN0 || new THREE.Vector3();
        this._lastN0.copy(f.n);
      } else {
        f.n.copy(prevN).addScaledVector(f.t, -prevN.dot(f.t)).normalize();
      }
      f.b.crossVectors(f.t, f.n).normalize();
      prevN = f.n;
    }
    const pos = this.bodyGeo.attributes.position, nor = this.bodyGeo.attributes.normal, hp = this.hullGeo.attributes.position;
    const squash = 1 + clamp(this.speed / 18, 0, 0.1);
    for (let i = 0; i < RINGS; i++) {
      const f = F[i];
      const u = i / (RINGS - 1);
      let r = radiusAt(u);
      // slight belly-bulge with the bound
      r *= 1 + Math.sin(u * Math.PI) * 0.06 * Math.sin(this.hopT * TAU);
      for (let j = 0; j < RAD; j++) {
        const phi = (j / RAD) * TAU;
        const c = Math.cos(phi), s = Math.sin(phi);
        const nx = f.n.x * c + f.b.x * s, ny = f.n.y * c + f.b.y * s, nz = f.n.z * c + f.b.z * s;
        const k = i * RAD + j;
        const ry = r * 0.95, rx = r * 1.05;
        const dx = f.n.x * c * ry + f.b.x * s * rx, dy = f.n.y * c * ry + f.b.y * s * rx, dz = f.n.z * c * ry + f.b.z * s * rx;
        pos.setXYZ(k, f.p.x + dx, f.p.y + dy, f.p.z + dz);
        nor.setXYZ(k, nx, ny, nz);
        hp.setXYZ(k, f.p.x + dx + nx * OUTLINE, f.p.y + dy + ny * OUTLINE, f.p.z + dz + nz * OUTLINE);
      }
    }
    pos.needsUpdate = true;
    nor.needsUpdate = true;
    hp.needsUpdate = true;
  }

  _updateLegs(dt, time) {
    const F = this.frames;
    const airborne = this.state === 'fling' || this.state === 'leap' || this.flip !== 0 || this.spin !== 0;
    const riding = this.state === 'ride';
    const sp = this.speed;
    for (const L of this.legs) {
      const ri = L.front ? Math.round(RINGS * 0.12) : Math.round(RINGS * 0.5);
      const f = F[ri];
      // socket on the underside, offset sideways
      const sock = _v.copy(f.p).addScaledVector(f.n, -0.18).addScaledVector(f.b, L.sx * 0.2);
      // foot target
      let fx, fy, fz;
      if (airborne || riding) {
        // tuck / dangle
        const t = time * 12 + L.sx * 2 + (L.front ? 0 : 1.3);
        fx = sock.x + f.t.x * 0.15 * (airborne ? 1 : 0) + Math.sin(t) * 0.1;
        fy = sock.y - (riding ? 0.35 : 0.3) + Math.cos(t) * 0.12;
        fz = sock.z + f.t.z * 0.15 * (airborne ? 1 : 0);
        if (riding) { fy = sock.y - 0.32; }
      } else {
        const gaitPhase = (this.gait * 1.0 + L.phase + (L.sx > 0 ? 0.0 : 0.0)) % 1;
        const swing = Math.cos(gaitPhase * TAU); // 1 forward .. -1 back
        const lift = Math.max(0, Math.sin(gaitPhase * TAU)) * clamp(0.1 + sp * 0.03, 0, 0.5);
        const stretch = 0.14 + clamp(sp * 0.03, 0, 0.32);
        // body-frame forward (head direction) is -tangent (tangent points neck->tail)
        const fdx = -f.t.x, fdz = -f.t.z;
        const fl = Math.hypot(fdx, fdz) || 1;
        fx = sock.x + (fdx / fl) * swing * stretch;
        fz = sock.z + (fdz / fl) * swing * stretch;
        const gy = Math.abs(this.lat) < 3.8 ? routeY(this.sW) : heightAt(fx, fz);
        fy = gy + 0.1 + lift;
        if (this.state === 'dance') fy += Math.max(0, Math.sin(time * 9.5 + L.phase * 6)) * 0.12;
        if (sock.y - fy > 0.8) fy = sock.y - 0.8; // legs cannot stretch forever
      }
      L.fp.set(fx, fy, fz);
      L.foot.position.copy(L.fp);
      // orient the capsule from socket to foot
      const dir = _v2.copy(L.fp).sub(sock);
      const len = dir.length();
      L.leg.position.copy(sock).addScaledVector(dir, 0.5);
      L.leg.scale.set(1, Math.max(0.1, len - 0.05), 1);
      _q.setFromUnitVectors(_up, dir.normalize());
      L.leg.quaternion.copy(_q);
      L.foot.quaternion.setFromAxisAngle(_up, Math.atan2(f.t.x, f.t.z) + Math.PI);
    }
  }

  _updateStars(dt, time) {
    const on = this.dizzy > 0.05;
    for (let i = 0; i < this.stars.length; i++) {
      const s = this.stars[i];
      s.visible = on;
      if (!on) continue;
      const a = time * 6 + (i / this.stars.length) * TAU;
      s.position.set(this.head.x + Math.cos(a) * 0.6, this.head.y + 0.75 + Math.sin(a * 2) * 0.05, this.head.z + Math.sin(a) * 0.6);
      s.material.opacity = this.dizzy;
    }
  }
}
